import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StickyBar from "@/components/StickyBar";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import HomePage from "@/pages/HomePage";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import CookiePolicy from "@/pages/CookiePolicy";

// Il modulo prenotazioni viaggia in chunk separati: chi visita la home non
// scarica una riga di codice del booking, e il punteggio del sito pubblico
// resta quello di prima.
const PrenotaPage = lazy(() => import("@/pages/PrenotaPage"));
const PrenotaEsito = lazy(() => import("@/pages/PrenotaEsito"));
const AdminPrenotazioni = lazy(() => import("@/pages/AdminPrenotazioni"));

/**
 * Cambiando pagina si riparte dall'alto.
 *
 * Serve perché i pulsanti "Prenota online" stanno in fondo alle sezioni: senza
 * questo, chi li preme dopo aver scorso mezza home page arriva su /prenota già
 * a metà, e vede il modulo dei dati invece della scelta del trattamento.
 *
 * Salta i link con ancora (#servizi, #contatti), che devono portare al loro
 * punto e non in cima, e rispetta chi ha chiesto meno animazioni.
 */
function VaiInCima() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) return;
    const preferisceFermo = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, left: 0, behavior: preferisceFermo ? "auto" : "instant" });
  }, [pathname, hash]);

  return null;
}

function Attesa() {
  return (
    <div
      className="flex min-h-[60vh] items-center justify-center bg-[var(--ee-ivory)]"
      role="status"
      aria-live="polite"
    >
      <span className="text-[14px] text-[var(--ee-ink-soft)]">Un istante…</span>
    </div>
  );
}

export default function App() {
  const { pathname } = useLocation();

  // Il pannello titolari è un'applicazione a sé: niente intestazione, piè di
  // pagina o barra CTA del sito vetrina.
  const areaAdmin = pathname.startsWith("/admin");
  // Il pannello porta con sé la propria struttura (header + main): qui basta un
  // contenitore neutro, altrimenti la pagina si ritrova due landmark <main>.
  const Contenitore = areaAdmin ? "div" : "main";
  // Su /prenota la barra CTA del sito duplicherebbe (e coprirebbe) la barra
  // azioni del flusso di prenotazione.
  const nascondiStickyBar = areaAdmin || pathname.startsWith("/prenota");

  return (
    <div
      className={[
        "App notranslate",
        areaAdmin ? "" : nascondiStickyBar ? "ee-safe-bottom-booking" : "ee-safe-bottom",
      ].join(" ")}
      translate="no"
      lang="it"
      data-testid="app-root"
    >
      <VaiInCima />
      {!areaAdmin && <Header />}

      <Contenitore>
        <Suspense fallback={<Attesa />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/prenota" element={<PrenotaPage />} />
            <Route path="/prenota/esito" element={<PrenotaEsito />} />
            <Route path="/admin/prenotazioni" element={<AdminPrenotazioni />} />
          </Routes>
        </Suspense>
      </Contenitore>

      {!areaAdmin && <Footer />}
      {!nascondiStickyBar && <StickyBar />}
      {!areaAdmin && <CookieConsentBanner />}
    </div>
  );
}
